/**
 * customer-app · HTML 片段模板（纯字符串，无 DOM 依赖）
 *
 * 来源：js/html-templates.js（已迁移）。
 *
 * 职责：挂 `window.RestaurantHtmlTemplates`，供 ui-render / ui-controller 生成 innerHTML。
 *
 * 兼容契约（必须保留）：
 *   - `window.RestaurantHtmlTemplates` 字段名 / 函数签名 / 输出 HTML 结构与原 IIFE 完全一致
 *   - `OS.registerModule('html', …)`
 *
 * 设计：
 *   - `Escaper` 由调用方注入（OS.escapeHtml），本模块不假设全局 escape
 *   - 与 `RestaurantAppConfig.LANG_PICKER` 类型对齐：`import type { LangPickerEntry } from './config'`
 */

import type { LangPickerEntry } from './config';

/** 调用方提供的 HTML 转义函数（通常为 OS.escapeHtml） */
export type Escaper = (raw: string) => string;

/* ─────────── 全局 facade（读取 LANG_PICKER） ─────────── */

interface RuntimeGlobal {
  RestaurantAppConfig?: {
    LANG_PICKER?: readonly LangPickerEntry[];
  };
  RestaurantHtmlTemplates?: RestaurantHtmlTemplates;
  OS?: {
    registerModule?: (name: string, ref: unknown) => unknown;
  };
}

const G = (typeof globalThis !== 'undefined' ? globalThis : window) as unknown as RuntimeGlobal;

/** 双引号属性中的 URL：仅转义 & 与 "，避免 data:image/svg+xml 等内含 < > 被全文 escapeHtml 破坏 */
export const escAttrUrl = (s: unknown): string =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;');

/** 空 src 在旧 WebKit 会当成当前文档 URL 去加载并报错；用 1×1 透明 GIF 占位 */
export const DISH_IMG_EMPTY_SRC =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

const DISH_ACTION_PLUS_ICON =
  '<svg class="pointer-events-none h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>';

/** menu.json 全图路径 assets/menu-dishes/ → 列表/购物车小图 assets/menu-dishes-lite/（与 customer-lite 同一约定；小图由 scripts/build_menu_dishes_lite.py 生成） */
export const dishThumbUrl = (url: string): string =>
  url.includes('assets/menu-dishes/') && !url.includes('assets/menu-dishes-lite/')
    ? url.split('assets/menu-dishes/').join('assets/menu-dishes-lite/')
    : url;

const DISH_IMG_ONERROR_FALLBACK =
  "var f=this.getAttribute('data-fallback-src');this.onerror=null;if(f)this.src=f;";

/**
 * 菜卡/购物车 `<img>` 的 src 属性串：优先小图（menu-dishes-lite），加载失败时
 * onerror 回退全图（与 customer-lite 的 data-fallback-src 模式一致）；详情大图不走此函数。
 */
export const dishCardImgSrcAttrs = (url: unknown): string => {
  const full = String(url ?? '').trim();
  if (!full) return `src="${DISH_IMG_EMPTY_SRC}"`;
  const thumb = dishThumbUrl(full);
  if (thumb === full) return `src="${escAttrUrl(full)}"`;
  return `src="${escAttrUrl(thumb)}" data-fallback-src="${escAttrUrl(full)}" onerror="${DISH_IMG_ONERROR_FALLBACK}"`;
};

export interface RoleTabButtonOpts {
  dataAttr: string;
  value: unknown;
  delayIdx: number;
  delayStepMs?: number;
  classBase: string;
  activeClass: string;
  idleClass: string;
  isActive: boolean;
  label: unknown;
}

export const roleTabButton = (esc: Escaper, o: RoleTabButtonOpts): string => {
  const {
    dataAttr,
    value,
    delayIdx,
    delayStepMs = 40,
    classBase,
    activeClass,
    idleClass,
    isActive,
    label,
  } = o;
  const v = esc(String(value ?? ''));
  const cls = `${classBase} ${isActive ? activeClass : idleClass}`;
  return `<button type="button" role="tab" ${dataAttr}="${v}" style="--os-delay:${delayIdx * delayStepMs}ms" class="os-fade-in-up os-press transition-all duration-200 active:scale-95 ${cls}">${esc(String(label ?? ''))}</button>`;
};

export const categoryNavWrapperMobile = (inner: string): string =>
  `<div class="flex w-max min-w-full flex-nowrap gap-2 sm:w-auto sm:flex-wrap" role="tablist">${inner}</div>`;

export const categoryNavWrapperTablet = (inner: string): string =>
  `<div class="flex flex-col gap-1.5" role="tablist">${inner}</div>`;

export const customerEmptyCategoryMessage = (esc: Escaper, text: string): string =>
  '<p class="col-span-full w-full rounded-2xl border border-dashed border-neutral-200/90 bg-white/60 py-16 text-center text-sm text-neutral-500 shadow-inner dark:border-neutral-700 dark:bg-neutral-900/40">' +
  esc(text) +
  '</p>';

export interface CustomerMenuCategorySectionOpts {
  domId: string;
  catId: unknown;
  title: string;
  gridClass: string;
  cardsHtml: string;
}

/** 「全部」视图下按分类分块，供滚动联动与 scrollIntoView */
export const customerMenuCategorySection = (esc: Escaper, o: CustomerMenuCategorySectionOpts): string => {
  const { domId, catId, title, gridClass, cardsHtml } = o;
  return `<section id="${esc(domId)}" data-menu-cat-section="${esc(String(catId))}" class="min-w-0 scroll-mt-[calc(4.75rem+env(safe-area-inset-top,0px))]">
    <h2 class="mb-5 inline-flex w-fit border-b-2 border-brand-400 pb-2 text-2xl font-extrabold normal-case leading-tight tracking-tight text-neutral-900 dark:text-neutral-100 osTablet:text-3xl">${esc(title)}</h2>
    <div class="${gridClass}">${cardsHtml}</div>
  </section>`;
};

export const customerCartEmptyMessage = (esc: Escaper, text: string): string =>
  '<p class="rounded-lg bg-neutral-100/50 py-8 text-center text-sm text-neutral-500 dark:bg-neutral-800/40">' + esc(text) + '</p>';

/** 与 js/config.ts LANG_PICKER 一致；config 未就绪时兜底 */
export const DEFAULT_LANG_PICKER: readonly LangPickerEntry[] = [
  { code: 'de', abbr: 'DE', flag: 'assets/flags/de.svg' },
  { code: 'en', abbr: 'EN', flag: 'assets/flags/gb.svg' },
  { code: 'zh', abbr: 'ZH', flag: 'assets/flags/cn.svg' },
  { code: 'es', abbr: 'ES', flag: 'assets/flags/es.svg' },
  { code: 'fr', abbr: 'FR', flag: 'assets/flags/fr.svg' },
  { code: 'it', abbr: 'IT', flag: 'assets/flags/it.svg' },
  { code: 'ja', abbr: 'JP', flag: 'assets/flags/jp.svg' },
  { code: 'tr', abbr: 'TR', flag: 'assets/flags/tr.svg' },
];

/** variant: 'login' | 'workbar' — 顾客端两处语言列表由 ui-controller 注入 */
export const customerLangPickerRowsHtml = (esc: Escaper, variant: 'login' | 'workbar'): string => {
  const cfg = G.RestaurantAppConfig?.LANG_PICKER;
  const rows = cfg && cfg.length ? cfg : DEFAULT_LANG_PICKER;
  const isWorkbar = variant === 'workbar';
  const btnCls = isWorkbar
    ? 'flex w-full items-center gap-2 px-3 py-3 text-left text-[0.704rem] font-medium text-neutral-900 hover:bg-neutral-50 dark:text-neutral-100 dark:hover:bg-neutral-800'
    : 'flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-neutral-900 hover:bg-neutral-50 dark:text-neutral-100 dark:hover:bg-neutral-800';
  const imgCls = isWorkbar
    ? 'h-[1.4rem] w-[2.4rem] rounded-sm object-cover ring-1 ring-black/10'
    : 'h-3.5 w-6 rounded-sm object-cover ring-1 ring-black/10';
  return rows
    .map(
      ({ code, abbr, flag }) =>
        `<button type="button" role="option" data-set-lang="${esc(String(code))}" class="${btnCls}"><img src="${esc(String(flag))}" alt="" class="${imgCls}" width="24" height="16" /><span class="font-medium">${esc(String(abbr))}</span></button>`,
    )
    .join('');
};

export interface CustomerDishCardOpts {
  id: unknown;
  index: number;
  gridLocked: boolean;
  imageUrl: unknown;
  categoryLine: string;
  title: string;
  desc: unknown;
  price: unknown;
  blockAdd: boolean;
  categoryId?: unknown;
  /** 管理端 `foodClass`；`veg` färbt Badge und Hinweis, nicht den Speisennamen. */
  foodClass?: unknown;
  /** 与 `dishFoodClassLabelForUi` 一致：素菜 / 荤菜 / 含鱼 等短标签，空则不出行 */
  foodClassLabel?: string;
}

const dishCardFoodClassBadgeHtml = (esc: Escaper, fc: unknown, label: unknown): string => {
  const t = String(label ?? '').trim();
  if (!t) return '';
  const raw = String(fc ?? '').trim();
  const tone =
    raw === 'veg'
      ? ' text-emerald-600 dark:text-emerald-400'
      : raw === 'meat'
        ? ' text-amber-600 dark:text-amber-500'
        : raw === 'fish'
          ? ' text-sky-600 dark:text-sky-400'
          : ' text-neutral-500 dark:text-neutral-400';
  return `<p class="mt-0.5 line-clamp-1 text-[10px] font-semibold uppercase tracking-wide${tone}">${esc(t)}</p>`;
};

const dishCardTitleClass = (_veg: boolean): string =>
  'mt-0.5 line-clamp-2 text-lg font-bold leading-snug text-neutral-900 dark:text-white';

const dishCardDescClassArticle = (veg: boolean): string =>
  veg
    ? 'mt-1 line-clamp-2 text-[11px] leading-relaxed text-emerald-600 dark:text-emerald-400 sm:text-xs md:text-sm'
    : 'mt-1 line-clamp-2 text-[11px] leading-relaxed text-neutral-500 dark:text-neutral-400 sm:text-xs md:text-sm';

const dishCardDescClassList = (veg: boolean): string =>
  veg
    ? 'mt-1 line-clamp-2 text-xs leading-relaxed text-emerald-600 dark:text-emerald-400'
    : 'mt-1 line-clamp-2 text-xs leading-relaxed text-neutral-500 dark:text-neutral-400';

export const customerDishCardArticle = (esc: Escaper, o: CustomerDishCardOpts): string => {
  const {
    id,
    index,
    gridLocked,
    imageUrl,
    categoryLine,
    title,
    desc,
    price,
    blockAdd,
    categoryId,
    foodClass,
    foodClassLabel,
  } = o;
  const veg = String(foodClass ?? '').trim() === 'veg';
  const catAttr = categoryId != null ? ` data-dish-category="${esc(String(categoryId))}"` : '';
  const cardCls = gridLocked
    ? 'pointer-events-none cursor-not-allowed opacity-50 grayscale-[0.35]'
    : 'cursor-default transition-all duration-200 hover:border-brand-200/80 hover:shadow-md dark:hover:border-brand-900/40';
  const overlayPlus =
    blockAdd
      ? ''
      : `<button type="button" tabindex="0" data-dish-detail-trigger="${esc(String(id))}" class="os-press absolute bottom-2.5 right-2.5 z-[3] inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border border-white/85 bg-brand-500/95 text-white shadow-lg shadow-black/20 ring-1 ring-black/5 backdrop-blur-sm transition-all duration-200 outline-none active:scale-95 hover:bg-brand-500 focus-visible:ring-2 focus-visible:ring-brand-200 dark:border-white/15 dark:bg-brand-500 dark:ring-white/10 dark:hover:bg-brand-400" aria-label="+">${DISH_ACTION_PLUS_ICON}</button>`;
  const descTrim = String(desc ?? '').trim();
  const descBlock =
    descTrim === ''
      ? ''
      : `<p class="${dishCardDescClassArticle(veg)}">${esc(descTrim)}</p>`;
  const priceNum = Number(price);
  const showPrice = Number.isFinite(priceNum) && priceNum !== 0;
  const priceBlock = showPrice
    ? `<div class="mt-auto flex items-end justify-start gap-1.5 pt-2"><span class="text-xl font-bold tabular-nums text-brand-600 dark:text-brand-400">€${esc(String(price))}</span></div>`
    : '';
  const fcBadge = dishCardFoodClassBadgeHtml(esc, foodClass, foodClassLabel);
  return `
          <article data-dish-card="${esc(String(id))}"${catAttr} style="--os-delay:${index * 45}ms" class="os-fade-in-up group flex h-full flex-col overflow-hidden rounded-2xl border border-neutral-100 bg-white p-0 shadow-md ring-1 ring-black/[0.04] transition-all duration-200 ease-out dark:border-neutral-700/80 dark:bg-neutral-900 dark:ring-white/[0.05] ${cardCls}">
            <div class="relative aspect-[1/1] min-h-[7.5rem] overflow-hidden bg-neutral-100 sm:aspect-[4/3] dark:bg-neutral-800" data-dish-card-media>
              <img ${dishCardImgSrcAttrs(imageUrl)} alt="" class="h-full w-full object-cover transition duration-500 ease-out group-hover:scale-105" loading="lazy" />
              <div class="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-90"></div>
              ${overlayPlus}
            </div>
            <div class="flex min-h-0 flex-1 flex-col p-2.5 sm:p-3.5">
              <p class="line-clamp-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400 sm:text-xs md:text-sm">${esc(categoryLine)}</p>
              <h3 class="${dishCardTitleClass(veg)}">${esc(title)}</h3>
              ${fcBadge}
              ${descBlock}
              ${priceBlock}
            </div>
          </article>`;
};

/** Smartphone: 横排一行（图左，文 + 价 +「+」右） */
export const customerDishCardListRow = (esc: Escaper, o: CustomerDishCardOpts): string => {
  const {
    id,
    index,
    gridLocked,
    imageUrl,
    categoryLine,
    title,
    desc,
    price,
    blockAdd,
    categoryId,
    foodClass,
    foodClassLabel,
  } = o;
  const veg = String(foodClass ?? '').trim() === 'veg';
  const catAttr = categoryId != null ? ` data-dish-category="${esc(String(categoryId))}"` : '';
  const rowCls = gridLocked
    ? 'pointer-events-none cursor-not-allowed opacity-50 grayscale-[0.35]'
    : 'cursor-default active:scale-[0.99] hover:border-brand-200/80 hover:shadow-md dark:hover:border-brand-900/40';
  const chevron =
    blockAdd
      ? ''
      : `<button type="button" tabindex="0" data-dish-detail-trigger="${esc(String(id))}" class="os-press relative z-[2] inline-flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full border border-white/85 bg-brand-500/95 text-white shadow-md shadow-black/15 ring-1 ring-black/5 transition-all duration-200 active:scale-95 hover:bg-brand-500 focus-visible:ring-2 focus-visible:ring-brand-200 dark:border-white/15 dark:bg-brand-500 dark:ring-white/10 dark:hover:bg-brand-400" aria-label="+">${DISH_ACTION_PLUS_ICON}</button>`;
  const descTrim = String(desc ?? '').trim();
  const descBlock =
    descTrim === ''
      ? ''
      : `<p class="${dishCardDescClassList(veg)}">${esc(descTrim)}</p>`;
  const priceNum = Number(price);
  const showPrice = Number.isFinite(priceNum) && priceNum !== 0;
  const priceRowCls = showPrice ? 'mt-auto flex items-end justify-between gap-2 pt-2' : 'mt-auto flex items-end justify-end gap-2 pt-2';
  const priceBlock = showPrice
    ? `<span class="text-base font-bold tabular-nums text-brand-600 dark:text-brand-400">€${esc(String(price))}</span>`
    : '';
  const fcBadgeList = dishCardFoodClassBadgeHtml(esc, foodClass, foodClassLabel);
  return `
          <article data-dish-card="${esc(String(id))}"${catAttr} style="--os-delay:${index * 35}ms" class="os-fade-in-up group flex w-full min-w-0 flex-row gap-3 overflow-hidden rounded-2xl border border-neutral-100 bg-white p-3 shadow-md shadow-neutral-900/5 ring-1 ring-black/[0.04] transition-all duration-300 ease-out dark:border-neutral-700/80 dark:bg-neutral-900 dark:shadow-black/25 dark:ring-white/[0.05] ${rowCls}">
            <div class="relative h-[5.25rem] w-[5.25rem] shrink-0 overflow-hidden rounded-xl bg-neutral-100 dark:bg-neutral-800 sm:h-24 sm:w-24" data-dish-card-media>
              <img ${dishCardImgSrcAttrs(imageUrl)} alt="" class="h-full w-full object-cover transition duration-500 ease-out group-hover:scale-105" loading="lazy" />
            </div>
            <div class="flex min-h-0 min-w-0 flex-1 flex-col py-0.5 pr-0.5">
              <p class="line-clamp-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">${esc(categoryLine)}</p>
              <h3 class="mt-0.5 line-clamp-2 text-sm font-bold leading-snug text-neutral-900 dark:text-white">${esc(title)}</h3>
              ${fcBadgeList}
              ${descBlock}
              <div class="${priceRowCls}">
                ${priceBlock}
                ${chevron}
              </div>
            </div>
          </article>`;
};

export const cartQtyButton = (
  esc: Escaper,
  kind: 'dec' | 'inc',
  dishId: unknown,
  cartLocked: boolean,
  compact: boolean,
): string => {
  const attr = kind === 'dec' ? 'data-dec' : 'data-inc';
  const sym = kind === 'dec' ? '−' : '+';
  const dis = cartLocked ? 'disabled' : '';
  const size = compact
    ? 'h-8 w-8 border border-neutral-200/90 text-base font-semibold shadow-sm dark:border-neutral-600'
    : 'h-12 w-12 border-2 border-neutral-200 text-xl font-semibold shadow-md dark:border-neutral-600';
  return `<button type="button" ${attr}="${esc(String(dishId))}" ${dis} class="os-press flex shrink-0 items-center justify-center rounded-full bg-white leading-none transition active:scale-95 hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-neutral-800 dark:hover:bg-neutral-700 ${size}">${sym}</button>`;
};

export interface CustomerCartLineRowOpts {
  ri: number;
  name: string;
  qty: unknown;
  line: unknown;
  dishId: unknown;
  imageUrl: unknown;
  cartLocked: boolean;
  readOnly: boolean;
  foodClass?: unknown;
}

/**
 * 购物车 / 订单 单行：统一「图片 → 数量 → 名称 → 金额 → (删除) 」顺序。
 * - 购物车（非只读）：数量用 dec/inc 控件、右侧额外一个 × 删除按钮。
 * - 订单 / 他人购物车（readOnly）：数量只读、无删除。
 */
export const customerCartLineRow = (esc: Escaper, o: CustomerCartLineRowOpts): string => {
  const { ri, name, qty, line, dishId, imageUrl, cartLocked, readOnly } = o;
  const nameCls = 'min-w-0 flex-1 truncate text-sm font-medium text-neutral-900 dark:text-white';
  const imgCls =
    'h-11 w-11 shrink-0 rounded-md border border-neutral-200/80 bg-neutral-100 object-cover dark:border-neutral-700/80 dark:bg-neutral-800';
  const imgBlock = `<img ${dishCardImgSrcAttrs(imageUrl)} alt="" class="${imgCls}" width="44" height="44" loading="lazy" />`;
  const qtyCtrl = readOnly
    ? `<span class="inline-flex min-w-[2.25rem] shrink-0 items-center justify-center rounded-md border border-neutral-200/85 bg-neutral-100 px-2 py-1 text-xs font-bold tabular-nums text-neutral-800 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100">${esc(String(qty))}×</span>`
    : `<div class="flex shrink-0 items-center gap-1">
              ${cartQtyButton(esc, 'dec', dishId, cartLocked, true)}
              <span class="min-w-[1.25rem] text-center text-sm font-semibold tabular-nums text-neutral-800 dark:text-neutral-200">${esc(String(qty))}</span>
              ${cartQtyButton(esc, 'inc', dishId, cartLocked, true)}
            </div>`;
  const delBtn = readOnly
    ? ''
    : `<button type="button" data-del="${esc(String(dishId))}" ${cartLocked ? 'disabled' : ''} aria-label="Löschen" class="os-press flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-red-200/80 bg-white text-red-600 transition hover:bg-red-50 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 dark:border-red-900/40 dark:bg-neutral-900 dark:text-red-400 dark:hover:bg-red-950/40">
          <svg viewBox="0 0 24 24" class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>`;
  /** 价钱小于 0 时整列价钱不渲染（菜单上的负价用作隐藏价位的占位）。 */
  const lineNum = Number(line);
  const priceCell =
    Number.isFinite(lineNum) && lineNum < 0
      ? ''
      : `<span class="shrink-0 min-w-[3rem] text-right text-sm font-semibold tabular-nums text-neutral-900 dark:text-white">€${esc(String(line))}</span>`;
  return `
          <div style="--os-delay:${ri * 35}ms" class="os-fade-in-up flex items-center gap-2 border-b border-neutral-100/90 py-2 last:border-b-0 dark:border-neutral-800/80">
            ${imgBlock}
            ${qtyCtrl}
            <p class="${nameCls}">${esc(name)}</p>
            ${priceCell}
            ${delBtn}
          </div>`;
};

/** 对外总表（与 window.RestaurantHtmlTemplates 一致） */
export interface RestaurantHtmlTemplates {
  roleTabButton: typeof roleTabButton;
  categoryNavWrapperMobile: typeof categoryNavWrapperMobile;
  categoryNavWrapperTablet: typeof categoryNavWrapperTablet;
  customerEmptyCategoryMessage: typeof customerEmptyCategoryMessage;
  customerMenuCategorySection: typeof customerMenuCategorySection;
  customerCartEmptyMessage: typeof customerCartEmptyMessage;
  customerLangPickerRowsHtml: typeof customerLangPickerRowsHtml;
  customerDishCardArticle: typeof customerDishCardArticle;
  customerDishCardListRow: typeof customerDishCardListRow;
  cartQtyButton: typeof cartQtyButton;
  customerCartLineRow: typeof customerCartLineRow;
}

const lib: RestaurantHtmlTemplates = {
  roleTabButton,
  categoryNavWrapperMobile,
  categoryNavWrapperTablet,
  customerEmptyCategoryMessage,
  customerMenuCategorySection,
  customerCartEmptyMessage,
  customerLangPickerRowsHtml,
  customerDishCardArticle,
  customerDishCardListRow,
  cartQtyButton,
  customerCartLineRow,
};

G.RestaurantHtmlTemplates = lib;

if (typeof G.OS?.registerModule === 'function') {
  try {
    G.OS.registerModule('html', lib);
  } catch {
    /* OS facade 注册失败不致命 */
  }
}
